/**
 * Web — Account-Deletion 2-stage modal.
 *
 * Stage 1: explanation + scary copy + "Weiter"
 * Stage 2: password input + literal "LÖSCHEN" confirmation
 *
 * On success: AuthContext.logout-equivalent cleanup + redirect to "/".
 * Apple Guideline 5.1.1(v) compliance.
 */
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { apiClient, STORAGE_KEYS, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "react-i18next";

export default function DeleteAccountDialog({ open, onClose }) {
  const { t } = useTranslation(["profile", "common", "auth"]);
  const CONFIRM_PHRASE = t("profile:deleteAccount.confirmPhrase");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stage, setStage] = useState(1);
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setStage(1);
    setPassword("");
    setPhrase("");
    setError(null);
    setBusy(false);
  }, []);

  const close = useCallback(() => {
    if (busy) return;
    reset();
    onClose();
  }, [busy, onClose, reset]);

  const submit = useCallback(async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      // axios uses `data:` for the body on DELETE
      await apiClient.delete("/auth/me", { data: { password } });
      // Wipe all stored auth; do NOT call /api/auth/logout (the session
      // is already revoked server-side via the cascade delete).
      localStorage.removeItem(STORAGE_KEYS.access);
      localStorage.removeItem(STORAGE_KEYS.refresh);
      localStorage.removeItem(STORAGE_KEYS.user);
      // Hard redirect to clear React state cleanly.
      window.location.assign("/?account_deleted=1");
    } catch (e) {
      setError(formatApiError(e));
      setBusy(false);
    }
  }, [busy, password]);

  if (!open) return null;

  const canSubmit =
    password.length >= 1 && phrase.trim().toUpperCase() === CONFIRM_PHRASE;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      data-testid="delete-account-overlay"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-rose-500/30 bg-zinc-950 p-6 shadow-2xl"
        data-testid="delete-account-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          disabled={busy}
          className="absolute right-3 top-3 rounded p-1 text-zinc-500 hover:text-zinc-200"
          data-testid="delete-account-close"
          aria-label={t("common:ui.close")}
        >
          <X size={18} />
        </button>

        <div className="mb-4 flex items-center gap-2 text-rose-300">
          <AlertTriangle size={20} />
          <h2 className="text-lg font-semibold">{t("profile:danger.delete_account")}</h2>
        </div>

        {stage === 1 && (
          <div data-testid="delete-account-stage-1" className="space-y-4">
            <p className="text-sm text-zinc-300">
              {t("profile:deleteAccount.confirmQuestion")}{" "}
              <strong>{t("profile:deleteAccount.irreversible")}</strong>.
            </p>
            <ul className="ml-4 list-disc space-y-1 text-xs text-zinc-400">
              <li>{t("profile:deleteAccount.bullet1Web")}</li>
              <li>{t("profile:deleteAccount.bullet3")}</li>
              <li>{t("profile:deleteAccount.bullet4Stripe")}</li>
              <li>{t("profile:deleteAccount.bullet5")}</li>
            </ul>
            {user?.subscription_status === "active" && (
              <div
                className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200"
                data-testid="delete-account-stripe-warning"
              >
                {t("profile:deleteAccount.stripeWarn")}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={close}
                className="flex-1 rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-900"
                data-testid="delete-account-cancel"
              >
                {t("common:ui.cancel")}
              </button>
              <button
                type="button"
                onClick={() => setStage(2)}
                className="flex-1 rounded-md border border-rose-500/50 bg-rose-500/10 px-4 py-2 text-sm font-medium text-rose-200 hover:bg-rose-500/20"
                data-testid="delete-account-continue"
              >
                {t("common:ui.next")}
              </button>
            </div>
          </div>
        )}

        {stage === 2 && (
          <form
            data-testid="delete-account-stage-2"
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) submit();
            }}
          >
            <p className="text-sm text-zinc-300">
              {t("profile:deleteAccount.stage2Intro")}{" "}
              <span className="font-mono text-rose-300">{CONFIRM_PHRASE}</span>
            </p>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-zinc-500">
                {t("auth:login.password")}
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                data-testid="delete-account-password-input"
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-rose-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-zinc-500">
                {t("profile:deleteAccount.confirmLabel")}
              </label>
              <input
                type="text"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                disabled={busy}
                placeholder={CONFIRM_PHRASE}
                data-testid="delete-account-phrase-input"
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm uppercase tracking-wider text-zinc-100 outline-none focus:border-rose-500"
                autoCapitalize="characters"
              />
            </div>

            {error && (
              <div
                className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2.5 text-xs text-rose-200"
                data-testid="delete-account-error"
              >
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStage(1)}
                disabled={busy}
                className="flex-1 rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-900 disabled:opacity-50"
                data-testid="delete-account-back"
              >
                {t("common:ui.back")}
              </button>
              <button
                type="submit"
                disabled={!canSubmit || busy}
                className="flex-1 rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="delete-account-submit"
              >
                {busy ? t("profile:deleteAccount.deleting") : t("profile:deleteAccount.deleteFinal")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
